import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { TFunction } from 'i18next'
import React from 'react'
import { Tooltip } from 'src/shared/components/tooltip'
import { IconAffiliate } from '../icons/icon-affiliate'
import { IconAutomationANdWorkflows } from '../icons/icon-automation-and-workflows'
import { IconAvailable } from '../icons/icon-available'
import { IconBlogs } from '../icons/icon-blogs'
import { IconBookingCalendar } from '../icons/icon-booking-calendar'
import { IconCommunities } from '../icons/icon-communities'
import { IconContactsAndCrm } from '../icons/icon-contacts-and-crm'
import { IconCourses } from '../icons/icon-courses'
import { IconECommerce } from '../icons/icon-e-commerce'
import { IconEmail } from '../icons/icon-email'
import { IconFunnel } from '../icons/icon-funnel'
import { IconIntegrations } from '../icons/icon-integrations'
import { IconLink } from '../icons/icon-link'
import { IconPayment } from '../icons/icon-payment'
import { IconPerks } from '../icons/icon-perks'
import { IconSMS } from '../icons/icon-sms'
import { IconSubaccounts } from '../icons/icon-subaccounts'
import { IconSupport } from '../icons/icon-support'
import { IconTeam } from '../icons/icon-team'
import { IconUnavailable } from '../icons/icon-unavailable'
import { IconWebsite } from '../icons/website'
import {
  PlanGroupIconType,
  PriceElementGroupInterface,
  PriceElementPeriodInterface,
} from '../price-element-interface'
import { CommonPriceElementUnknownUi } from '../ui'
import {
  PriceGroupHeaderIconUi,
  PriceGroupHeaderTitleUi,
  PriceGroupHeaderUi,
} from './ui/price-group-header'
import {
  PriceGroupItemPlanTitleUi,
  PriceGroupItemPlanUi,
  PriceGroupItemTitleUi,
  PriceGroupItemUi,
} from './ui/price-group-items'

interface CommonPriceGroupProps {
  data: PriceElementGroupInterface
  locale: PageLocaleEnum
  pricePeriod: PriceElementPeriodInterface[]
  t: TFunction
  isLoading: boolean
}

export const CommonPriceElementGroup = ({
  data,
  pricePeriod,
  t,
  isLoading,
}: CommonPriceGroupProps) => {
  const getIcon = (icon: PlanGroupIconType) => {
    switch (icon) {
      case 'contactsAndCrm':
        return <IconContactsAndCrm />
      case 'email':
        return <IconEmail />
      case 'automationsAndWorkflows':
        return <IconAutomationANdWorkflows />
      case 'funnel':
        return <IconFunnel />
      case 'booking':
        return <IconBookingCalendar />
      case 'payment':
        return <IconPayment />
      case 'blogs':
        return <IconBlogs />
      case 'link':
        return <IconLink />
      case 'website':
        return <IconWebsite />
      case 'e-commerce':
        return <IconECommerce />
      case 'courses':
        return <IconCourses />
      case 'communities':
        return <IconCommunities />
      case 'affiliate':
        return <IconAffiliate />
      case 'support':
        return <IconSupport />
      case 'team':
        return <IconTeam />
      case 'integrations':
        return <IconIntegrations />
      case 'subaccounts':
        return <IconSubaccounts />
      case 'perks':
        return <IconPerks />
      case 'sms_marketing':
        return <IconSMS />
      default:
        break
    }
  }

  const getPlanContent = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? <IconAvailable /> : <IconUnavailable />
    }

    if (value === 'unknown') {
      return ''
    }

    if (value === 'Unlimited') {
      return t('components.price_element.unlimited')
    }

    return value
  }

  return (
    <>
      <PriceGroupHeaderUi>
        <PriceGroupHeaderIconUi>{getIcon(data.icon)}</PriceGroupHeaderIconUi>
        {data.tooltip ? (
          <Tooltip
            message={data.tooltip}
            messageColor="#181c39"
            size={18}
            buttonColor="#181c39"
            btnStyles={{
              position: 'relative',
              top: '1px',
              left: '15px',
            }}
          >
            <PriceGroupHeaderTitleUi>{t(data.title)}</PriceGroupHeaderTitleUi>
          </Tooltip>
        ) : (
          <PriceGroupHeaderTitleUi>{t(data.title)}</PriceGroupHeaderTitleUi>
        )}
      </PriceGroupHeaderUi>

      {data.items.map(el => (
        <PriceGroupItemUi key={el.title} planCount={pricePeriod.length}>
          <PriceGroupItemTitleUi>{t(el.title)}</PriceGroupItemTitleUi>
          {el.plans.map((plan, idx) => {
            const period = pricePeriod[idx]

            if (!period) {
              return null
            }

            return (
              <PriceGroupItemPlanUi key={`${period.id}-${idx}`}>
                <PriceGroupItemPlanTitleUi>
                  {`${period.title}: `}
                </PriceGroupItemPlanTitleUi>
                {getPlanContent(plan) ? (
                  getPlanContent(plan)
                ) : (
                  <CommonPriceElementUnknownUi width="100px" />
                )}
              </PriceGroupItemPlanUi>
            )
          })}
        </PriceGroupItemUi>
      ))}
    </>
  )
}
